import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Box, Group, Loader, Paper, Text, UnstyledButton } from '@mantine/core';
import { IoClose, IoImageOutline, IoSend } from 'react-icons/io5';
import { Button, Textarea } from '@shared/ui';
import { ChatBubbleImages } from '@modules/client-support-chat/components/ChatBubbleImages';
import {
  sellerSupportChatApi,
  SELLER_SUPPORT_CHAT_MAX_IMAGES,
} from '@modules/seller-support-chat/api/seller-support-chat.api';
import {
  appendSellerSupportMessageToCache,
  chatMessagesQueryKey,
  CHAT_THREADS_QUERY_KEY,
  CHAT_UNREAD_QUERY_KEY,
  mergeSellerSupportMessages,
} from '@modules/seller-support-chat/chat-cache';
import { useSellerSupportChatSocket } from '@modules/seller-support-chat/hooks/useSellerSupportChatSocket';
import { ShopAvatar } from '@modules/seller-support-chat/components/ShopAvatar';
import type { SellerSupportMessage, SellerSupportMessagesResponse } from '@modules/seller-support-chat/types';
import './SellerSupportChatPanel.css';

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function chatDayKey(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function chatTime(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function chatDayLabel(iso: string) {
  const key = chatDayKey(iso);
  const today = chatDayKey(new Date().toISOString());
  const yesterday = chatDayKey(new Date(Date.now() - 86_400_000).toISOString());
  if (key === today) return 'Сегодня';
  if (key === yesterday) return 'Вчера';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return key;
  const withYear = d.getFullYear() !== new Date().getFullYear();
  return withYear
    ? `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`
    : `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}`;
}

function messageKey(message: SellerSupportMessage) {
  return `${message.id}:${message.createdAt}`;
}

type PendingImage = {
  id: string;
  file: File;
  previewUrl: string;
};

type SellerSupportChatPanelProps = {
  sellerId: number;
  shopName?: string | null;
  shopLogoUrl?: string | null;
  compact?: boolean;
};

export function SellerSupportChatPanel({
  sellerId,
  shopName,
  shopLogoUrl,
  compact = false,
}: SellerSupportChatPanelProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const queryKey = chatMessagesQueryKey(sellerId);

  const messagesQuery = useQuery({
    queryKey,
    queryFn: async () => {
      const data = await sellerSupportChatApi.getMessages(sellerId, { limit: 100 });
      const prev = queryClient.getQueryData<SellerSupportMessagesResponse>(queryKey);
      return mergeSellerSupportMessages(prev, data);
    },
    enabled: sellerId > 0,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const markReadMutation = useMutation({
    mutationFn: () => sellerSupportChatApi.markRead(sellerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CHAT_THREADS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: CHAT_UNREAD_QUERY_KEY });
    },
  });

  const clearPendingImages = useCallback(() => {
    setPendingImages((prev) => {
      prev.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return [];
    });
  }, []);

  const sendMutation = useMutation({
    mutationFn: ({ body, images }: { body: string; images: File[] }) =>
      sellerSupportChatApi.sendMessage(sellerId, body, images),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
    },
    onSuccess: (message) => {
      setDraft('');
      clearPendingImages();
      appendSellerSupportMessageToCache(queryClient, sellerId, message);
      queryClient.invalidateQueries({ queryKey: CHAT_THREADS_QUERY_KEY });
    },
  });

  const appendMessage = useCallback(
    (message: SellerSupportMessage, messageSellerId: number) => {
      if (messageSellerId !== sellerId) return;
      appendSellerSupportMessageToCache(queryClient, sellerId, message);
      if (message.senderKind === 'SELLER') {
        markReadMutation.mutate();
      }
    },
    [markReadMutation, queryClient, sellerId],
  );

  useSellerSupportChatSocket({
    onMessage: ({ sellerId: messageSellerId, message }) =>
      appendMessage(message, messageSellerId),
    onUnread: ({ scope, count, sellerId: unreadSellerId }) => {
      if (scope !== 'admin') return;
      queryClient.invalidateQueries({ queryKey: CHAT_THREADS_QUERY_KEY });
      if (unreadSellerId === sellerId) return;
      queryClient.setQueryData(CHAT_UNREAD_QUERY_KEY, { count });
    },
  });

  useEffect(() => {
    if (!messagesQuery.isSuccess) return;
    markReadMutation.mutate();
  }, [messagesQuery.isSuccess, sellerId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messagesQuery.data?.items.length, sendMutation.isSuccess, pendingImages.length]);

  useEffect(() => {
    clearPendingImages();
    setDraft('');
  }, [sellerId, clearPendingImages]);

  useEffect(() => {
    return () => {
      pendingImages.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- revoke on unmount only
  }, []);

  const messages = messagesQuery.data?.items ?? [];
  const sortedMessages = useMemo(
    () => [...messages].sort((a, b) => a.id - b.id),
    [messages],
  );

  const handlePickImages = (files: FileList | null) => {
    if (!files?.length) return;
    const next: PendingImage[] = [];
    const room = SELLER_SUPPORT_CHAT_MAX_IMAGES - pendingImages.length;
    Array.from(files)
      .filter((file) => file.type.startsWith('image/'))
      .slice(0, Math.max(room, 0))
      .forEach((file) => {
        next.push({
          id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
          file,
          previewUrl: URL.createObjectURL(file),
        });
      });
    if (next.length === 0) return;
    setPendingImages((prev) => [...prev, ...next].slice(0, SELLER_SUPPORT_CHAT_MAX_IMAGES));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingImage = (id: string) => {
    setPendingImages((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.id !== id);
    });
  };

  const handleSend = () => {
    const body = draft.trim();
    const images = pendingImages.map((item) => item.file);
    if ((!body && images.length === 0) || sendMutation.isPending) return;
    sendMutation.mutate({ body, images });
  };

  const canSend = Boolean(draft.trim() || pendingImages.length > 0);
  const headerTitle = shopName || `Продавец #${sellerId}`;

  return (
    <Paper withBorder radius="md" className={`seller-support-chat-panel ${compact ? 'seller-support-chat-panel--compact' : ''}`}>
      <UnstyledButton
        className="seller-support-chat-panel__header"
        onClick={() => navigate(`/sellers/${sellerId}`)}
        title="Открыть страницу продавца"
      >
        <Group wrap="nowrap" gap="sm">
          <ShopAvatar shopName={headerTitle} logoUrl={shopLogoUrl} size={40} />
          <div className="seller-support-chat-panel__header-text">
            <Text fw={600}>{headerTitle}</Text>
            <Text size="xs" c="dimmed">
              Чат с продавцом
            </Text>
          </div>
        </Group>
      </UnstyledButton>
      <Box ref={scrollRef} className="seller-support-chat-panel__messages">
        {messagesQuery.isLoading ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : null}
        {!messagesQuery.isLoading && sortedMessages.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            Сообщений пока нет. Напишите продавцу первым.
          </Text>
        ) : null}
        {sortedMessages.map((message, index) => {
          const mine = message.senderKind === 'SUPER_ADMIN';
          const images = message.imageUrls ?? [];
          const prev = sortedMessages[index - 1];
          const showDay =
            !prev || chatDayKey(prev.createdAt) !== chatDayKey(message.createdAt);
          const stacked = Boolean(prev && !showDay && prev.senderKind === message.senderKind);
          return (
            <div key={messageKey(message)} className="seller-support-chat-panel__item">
              {showDay ? (
                <div className="seller-support-chat-panel__day">
                  <span>{chatDayLabel(message.createdAt)}</span>
                </div>
              ) : null}
              <div
                className={[
                  'seller-support-chat-panel__bubble',
                  mine
                    ? 'seller-support-chat-panel__bubble--mine'
                    : 'seller-support-chat-panel__bubble--theirs',
                  stacked ? 'seller-support-chat-panel__bubble--stacked' : '',
                  images.length > 0 ? 'seller-support-chat-panel__bubble--has-images' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {stacked ? null : (
                  <div className="seller-support-chat-panel__bubble-author">
                    {mine ? 'Вы' : message.senderName || 'Продавец'}
                  </div>
                )}
                {images.length > 0 ? (
                  <ChatBubbleImages
                    urls={images}
                    classPrefix="seller-support-chat-panel"
                  />
                ) : null}
                <div className="seller-support-chat-panel__bubble-body">
                  {message.body ? (
                    <div className="seller-support-chat-panel__bubble-text">{message.body}</div>
                  ) : null}
                  <time
                    className="seller-support-chat-panel__bubble-time"
                    dateTime={message.createdAt}
                  >
                    {chatTime(message.createdAt)}
                  </time>
                </div>
              </div>
            </div>
          );
        })}
      </Box>
      <Box className="seller-support-chat-panel__composer">
        {pendingImages.length > 0 ? (
          <div className="seller-support-chat-panel__composer-previews">
            {pendingImages.map((item) => (
              <div key={item.id} className="seller-support-chat-panel__composer-preview">
                <img src={item.previewUrl} alt="" />
                <button
                  type="button"
                  className="seller-support-chat-panel__composer-preview-remove"
                  onClick={() => removePendingImage(item.id)}
                  aria-label="Убрать фото"
                >
                  <IoClose />
                </button>
              </div>
            ))}
          </div>
        ) : null}
        <div className="seller-support-chat-panel__composer-row">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(event) => handlePickImages(event.currentTarget.files)}
          />
          <Button
            type="button"
            variant="secondary"
            className="seller-support-chat-panel__composer-attach"
            onClick={() => fileInputRef.current?.click()}
            disabled={
              sendMutation.isPending || pendingImages.length >= SELLER_SUPPORT_CHAT_MAX_IMAGES
            }
            aria-label="Прикрепить фото"
          >
            <IoImageOutline />
          </Button>
          <Textarea
            className="seller-support-chat-panel__composer-input"
            placeholder="Сообщение продавцу…"
            minRows={1}
            maxRows={6}
            autosize
            value={draft}
            onChange={(event) => setDraft(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                handleSend();
              }
            }}
          />
          <Button
            className="seller-support-chat-panel__composer-send"
            onClick={handleSend}
            loading={sendMutation.isPending}
            disabled={!canSend}
            aria-label="Отправить"
          >
            <IoSend />
          </Button>
        </div>
      </Box>
    </Paper>
  );
}
